/**
 * Applies getSessionState() snapshot to Zustand (artifacts, cost, deps, active plan).
 */
import { useDevAgentStore } from "@/stores/devAgentStore";
import { getSessionState, type DevAgentSessionState } from "../devAgentService";

export async function restoreDevAgentSessionState(sessionUuid: string): Promise<void> {
  try {
    const state: DevAgentSessionState = await getSessionState(sessionUuid);
    if (useDevAgentStore.getState().sessionUuid !== sessionUuid) return;

    const store = useDevAgentStore.getState();

    if (Array.isArray(state.artifacts) && state.artifacts.length > 0) {
      const newPinned: Record<string, boolean> = { ...store.pinnedArtifactIds };
      for (const a of state.artifacts) {
        newPinned[a.id || a.name] = true;
        store.setArtifactBadge(a.id || a.name, "R");
      }
      useDevAgentStore.setState({ pinnedArtifactIds: newPinned });
    }

    if (state.cost) {
      useDevAgentStore.setState({ sessionCost: state.cost });
    }

    if (state.dependencies) {
      useDevAgentStore.getState().setDependencies(state.dependencies as any);
    }

    if (state.impact_analysis?.class_name) {
      const className = state.impact_analysis.class_name;
      const exists = store.feedItems.some(
        (f) =>
          f.type === "impact_analysis" &&
          (f.data?.class_name === className || f.data?.className === className),
      );
      if (!exists) {
        store.addFeedItem({
          type: "impact_analysis",
          data: state.impact_analysis,
        });
      }
    }

    if (state.test_coverage?.class_name) {
      const className = state.test_coverage.class_name;
      const exists = store.feedItems.some(
        (f) =>
          f.type === "test_coverage" &&
          (f.data?.class_name === className || f.data?.className === className),
      );
      if (!exists) {
        store.addFeedItem({
          type: "test_coverage",
          data: state.test_coverage,
        });
      }
    }

    // Re-hydrate persisted test runs into the store. Backend persists last 10
    // in session.metadata_["test_runs"].
    if (Array.isArray(state.test_runs) && state.test_runs.length > 0) {
      useDevAgentStore.setState({
        testRuns: state.test_runs.map((tr: any) => ({
          testClassName: tr.class_name,
          className: tr.production_class ?? null,
          coveredClasses: Array.isArray(tr.covered_classes) ? tr.covered_classes : [],
          success: !!tr.success,
          summary: tr.result || (tr.success ? "Tests passed" : "Tests failed"),
          rawResult: tr.result,
          passed: tr.passed,
          failed: tr.failed,
          total: tr.total,
          ts: tr.ts || Date.now(),
        })),
      });
    }

    const subtasks = state.active_plan?.subtasks;
    if (subtasks && subtasks.length > 0) {
      const plan = state.active_plan!;
      const steps = subtasks.map((s: any, i: number) => ({
        step_number: s.step_number || i + 1,
        description: s.description || `Step ${i + 1}`,
        status: s.status || "pending",
      }));
      const existingPlan = store.feedItems.find((f) => f.type === "plan");
      if (!existingPlan) {
        store.addFeedItem({
          type: "plan",
          data: {
            steps,
            summary: plan.summary || "Plan",
            total: steps.length,
            isApproved: plan.status === "executing",
            currentStep:
              steps.findIndex((s: any) => s.status === "running") + 1 ||
              steps.filter((s: any) => s.status === "completed").length,
          },
        });
        if (plan.status === "pending_approval") {
          store.addFeedItem({
            type: "approval",
            data: {
              approvalId: `plan-${sessionUuid}`,
              action: "execute_plan",
              description: `Execute this ${steps.length}-step plan?`,
              changesPreview: steps.map((s: any) => ({
                file: s.description.slice(0, 60),
                type: "step",
                action: `Step ${s.step_number}`,
              })),
            },
          });
        }
      }
    }
  } catch {
    /* session state load not critical */
  }
}
